import $http from '@vue-util/http';
import { useThrottleFn } from '@vueuse/core';
import { ref } from 'vue';
import { useStore } from 'vuex';
import { fireATBLinkTag, fireErrorLinkTagging } from '../analytics/prosFBTEvent';
import { getShop5ExploreAttributes, setATBCookies } from '../utils/utils';

export const getUpcId = (product, prosFBTTwoProductRecEnabled = false) => {
  if (prosFBTTwoProductRecEnabled) {
    return `${product.traits.selectedUpc}`;
  }

  return `${Object.keys(product.relationships.upcs)[0]}`;
};

export const getProdSelectionInfo = (product) => {
  if (!product || Object.keys(product).length === 0) {
    return '';
  }

  const todaysDate = new Date();

  return Object.entries({
    cmexplore: getShop5ExploreAttributes(product).join('-_-'),
    // This extra `&` is required for Shop5 tag in bag page
    '&PseudoCat': product.relationships?.taxonomy?.defaultCategoryId,
    PseudoProdID: product.id,
    productVideo: product.imagery?.videoId && 'ENHANCED_FEATURES_VIDEO',
    BagDate: [todaysDate.getMonth() + 1, todaysDate.getDate(), todaysDate.getFullYear()].join('/'),
    breadCrumbCategory: product.detail?.typeName,
    productCategoryName: product.detail?.typeName,
    productSize: product.traits?.sizes?.selectedSize?.name || '',
  }).filter((keyValPair) => keyValPair[1]) // removes null values
    .map((keyValPair) => `${keyValPair[0]}=${encodeURIComponent(keyValPair[1])}`) // makes array of "key=val"
    .join('&');
};

export const buildResponsiveAddToBagRequest = (product, prosFBTTwoProductRecEnabled = false) => {
  const upcId = getUpcId(product, prosFBTTwoProductRecEnabled);

  const upcNumber = product.relationships.upcs[upcId].identifier?.upcNumber || '';
  return {
    item: {
      source: 'PDPA2B',
      upcId,
      upcNumber,
      prodSelectionInfo: getProdSelectionInfo(product),
      quantity: 1,
    },
  };
};

export const checkForErrors = (response) => {
  const metaErrorMessage = response.bag?.meta?.messages?.error || '';
  const bagItems = response.bag?.sections?.bagItems?.items;
  const bagItemErrorMessage = (bagItems && bagItems[0].messages && bagItems[0].messages.error) || false;
  return Boolean(metaErrorMessage || bagItemErrorMessage);
};

export const validateBagResponse = (response) => {
  if (Object.keys(response).length === 0 || checkForErrors(response)) {
    return false;
  }
  setATBCookies(response);
  return true;
};

export const addItemToBag = (productModel, prosFBTTwoProductRecEnabled = false) => {
  const url = '/xapi/bag/v1/add';
  const headers = {
    dataType: 'application/json',
  };
  const payload = buildResponsiveAddToBagRequest(productModel, prosFBTTwoProductRecEnabled);
  return new Promise((resolve, reject) => {
    $http.post(url, payload, headers).then((response) => {
      resolve(response.data);
    }).catch((e) => {
      reject(e);
    });
  });
};

export const addAllItemsToBag = (productIds, thumbs, prosFBTTwoProductRecEnabled = false) => new Promise((resolve, reject) => {
  const addedItems = [];
  // eslint-disable-next-line
    productIds.reduce((previousPromise, nextID) => previousPromise.then(() => addItemToBag(thumbs[nextID], prosFBTTwoProductRecEnabled).then((response) => {
    if (validateBagResponse(response)) {
      addedItems.push(response);
    } else {
      reject(addedItems);
    }
  })).catch(() => {
    reject(addedItems);
  }), Promise.resolve()).then(() => {
    resolve(addedItems);
  });
});

export const removeProductFromBag = (data) => {
  const bagItem = data.bag.sections.bagItems.items ? data.bag.sections.bagItems.items[0] : null;
  if (!bagItem) {
    return Promise.reject(new Error('Empty bag'));
  }

  const { bagGuid } = data.bag.meta;
  const url = `/xapi/bag/v1/${bagGuid}/items/${bagItem.sequenceNumber}`;
  const headers = {
    dataType: 'application/json',
  };
  const reqData = { item: { upcId: bagItem.upcID, gwpIndicator: false } };
  return new Promise((resolve, reject) => {
    $http.delete(url, reqData, headers).then((response) => {
      resolve(response.data);
    }).catch(() => {
      reject();
    });
  });
};

export const removeProductsFromBag = (bagItems) => new Promise((resolve, reject) => {
  Promise.all(bagItems.map((bagItem) => removeProductFromBag(bagItem))).then(() => {
    resolve();
  }).catch(() => {
    reject();
  });
});

export const useAddToBag = () => {
  const gStore = useStore();
  const productData = ref([]);
  const productIds = ref([]);
  const toggleLoading = ref(false);
  const toggleErrorMessage = ref(false);

  const onAddToBagClick = useThrottleFn(() => {
    const productDataObject = {};
    productData.value.forEach((element) => {
      productDataObject[element.id] = element;
    });
    const ids = productIds.value.map((i) => +i.split('-_-')[0]);
    toggleLoading.value = true;
    return addAllItemsToBag(ids, productDataObject, gStore?.state?.recommendationStore?.properties?.prosFBTTwoProductRecEnabled)
      .then((items) => {
        fireATBLinkTag(items, ids[0]);
        toggleLoading.value = false;
        setTimeout(() => {
          window.location.href = '/my/bag';
        }, 400); // allow to finish analytics tags
      }).catch((items) => {
        if (items && items.length > 0) {
          removeProductsFromBag(items);
        }
        fireErrorLinkTagging(ids, productDataObject, ids[0]);
        toggleLoading.value = false;
        toggleErrorMessage.value = true;
      });
  });
  return {
    productData, productIds, toggleLoading, toggleErrorMessage, onAddToBagClick,
  };
};

export default useAddToBag;
